import styles from './portfolio-guide.module.scss';
import Image from '@/components/common/SpiritoryImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRightToBracket, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import SearchInGuides from '@/components/common/searchInGuides';
import portfolioGuideImage from '@/public/images/portfolio-guide/portfolio-guide.webp';
import { useRouter } from 'next/router';
import mainImagePhones from '@/public/images/portfolio-guide/mainImageLowRes.webp';
import Head from 'next/head';
import BreadcrumbStructuredData from '@/components/structured_data/BreadcrumbStructuredData';
import Accordion from 'react-bootstrap/Accordion';
import { useState } from 'react';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import useBackendUser from '@/hooks/useBackendUser';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

const PortfolioGuide = () => {
  const { data: user } = useBackendUser();
  const [open, setOpen] = useState({
    num1: 'none',
    num2: 'none',
    num3: 'none',
  });
  const { t } = useTranslation('portfolio_guide');
  const { t: tOther } = useTranslation('other_translations');
  const { t: tCommon } = useTranslation('common');

  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>
          {translationFallback('portfolio_title', tCommon('portfolio_title'), 'Portfolio Guide')}
        </title>
        <meta
          name="description"
          content={translationFallback(
            'portfolio_description',
            tCommon('portfolio_description'),
            'Use our portfolio function to track and actively manage the value, composition and price development of your collection free of charge'
          )}
        />
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <BreadcrumbStructuredData
          items={[
            { name: 'Home', item: 'https://spiritory.com' },
            {
              name: translationFallback(
                'portfolio_title',
                tCommon('portfolio_title'),
                'Portfolio Guide'
              ),
              item: canonicalUrl,
            },
          ]}
        />
      </Head>
      <div className={styles.mainContainer}>
        <div className={`bg-blue-gradient ${styles.mainSection}`}>
          <div className="d-flex align-items-center justify-content-between h-100">
            <div className={`d-flex align-items-start gap-3 ${styles.mainTextContainer}`}>
              <div className={styles.hideMobile}>
                <Image
                  src="/images/howItWorks/icon-portfolio.svg"
                  alt="tag-icon"
                  width="57"
                  height="57"
                  style={{
                    objectFit: 'contain',
                    position: 'relative',
                    bottom: 5,
                  }}
                />
              </div>
              <div className={`${styles.hideDesktop} d-flex flex-column align-items-center gap-2`}>
                <div className="d-flex align-items-start gap-2" style={{ maxWidth: 450 }}>
                  <div>
                    <Image
                      src="/images/howItWorks/icon-portfolio.svg"
                      alt="tag-icon"
                      width="57"
                      height="57"
                      style={{
                        objectFit: 'contain',
                        position: 'relative',
                        bottom: 5,
                      }}
                    />
                  </div>
                  <div>
                    <h1 className="h2 p-0 m-0">
                      {translationFallback(
                        'portfolioGuideTitle',
                        t('portfolioGuideTitle'),
                        'Portfolio Guide'
                      )}
                    </h1>
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCheck} className="text-primary" />
                        <p className="font-size-20 lh-23 p-0 m-0">
                          {translationFallback(
                            'trackYourCollection',
                            t('trackYourCollection'),
                            'Track your collection'
                          )}
                        </p>
                      </div>
                    </div>
                    <div className={`d-flex align-items-center gap-2`}>
                      <FontAwesomeIcon icon={faCheck} className="text-primary" />
                      <p className="font-size-20 lh-23 m-0 p-0">
                        {translationFallback(
                          'statisticsDataInsights',
                          t('statisticsDataInsights'),
                          'All statistics, data and insights at your fingertips'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className={`d-flex align-items-center justify-content-between flex-wrap mt-3 ${styles.buttonContainer}`}
                  style={{ width: '100%' }}
                >
                  <div
                    style={{ width: '48%' }}
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-in');
                      }
                    }}
                  >
                    <button
                      className={`d-flex align-items-center gap-2 btn btn-s-outline-primary px-7`}
                      style={{ padding: '0.7rem 0' }}
                      type="button"
                    >
                      <span>
                        <FontAwesomeIcon icon={faRightToBracket} />
                      </span>
                      {translationFallback('logInButtonText', t('logInButtonText'), 'Log In')}
                    </button>
                  </div>
                  <div
                    style={{ width: '48%' }}
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-up');
                      }
                    }}
                  >
                    <button
                      className={`d-flex align-items-center gap-2 btn btn-s-primary px-7`}
                      style={{ padding: '0.7rem 0' }}
                      type="button"
                    >
                      <span>
                        <FontAwesomeIcon icon={faUserPlus} />
                      </span>
                      {translationFallback('signUpButtonText', t('signUpButtonText'), 'Sign Up')}
                    </button>
                  </div>
                </div>
              </div>
              <div className={`d-flex flex-column align-items-start gap-2 ${styles.hideMobile}`}>
                <h1 className="h2 p-0 m-0">
                  {translationFallback(
                    'portfolioGuideTitle',
                    t('portfolioGuideTitle'),
                    'Portfolio Guide'
                  )}
                </h1>
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faCheck} className="text-primary" />
                    <p className="font-size-20 lh-23 p-0 m-0">
                      {translationFallback(
                        'trackYourCollection',
                        t('trackYourCollection'),
                        'Track your collection'
                      )}
                    </p>
                  </div>
                </div>
                <div className={`d-flex align-items-center gap-2`}>
                  <FontAwesomeIcon icon={faCheck} className="text-primary" />
                  <p className="font-size-20 lh-23 m-0 p-0">
                    {translationFallback(
                      'statisticsDataInsights',
                      t('statisticsDataInsights'),
                      'All statistics, data and insights at your fingertips'
                    )}
                  </p>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
                  <div
                    style={{ maxWidth: 300 }}
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-in');
                      }
                    }}
                  >
                    <button
                      className={`d-flex align-items-center gap-2 btn btn-s-outline-primary px-7`}
                      style={{ padding: '0.7rem 0' }}
                      type="button"
                    >
                      <span>
                        <FontAwesomeIcon icon={faRightToBracket} />
                      </span>
                      {translationFallback('logInButtonText', t('logInButtonText'), 'Log In')}
                    </button>
                  </div>
                  <div
                    style={{ maxWidth: 300 }}
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-up');
                      }
                    }}
                  >
                    <button
                      className={`d-flex align-items-center gap-2 btn btn-s-primary px-7`}
                      style={{ padding: '0.7rem 0' }}
                      type="button"
                    >
                      <span>
                        <FontAwesomeIcon icon={faUserPlus} />
                      </span>
                      {translationFallback('signUpButtonText', t('signUpButtonText'), 'Sign Up')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.mainImageContainer}>
              <Image
                src={portfolioGuideImage}
                alt="example"
                width={1000}
                height={1000}
                sizes="100vw"
                className={styles.hideMobile}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              <Image
                src={mainImagePhones}
                alt="example"
                width={1000}
                height={1000}
                sizes="100vw"
                className={styles.hideDesktop}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
        <div className={`py-8 ${styles.secondSection}`}>
          <div className="d-flex flex-column align-items-center justify-content-center">
            <div
              style={{ width: 'fit-content' }}
              className="d-flex flex-column align-items-center gap-4"
            >
              <div>
                <h2
                  className="h3 mt-3"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback(
                    'worldOfCollectingTitle',
                    t('worldOfCollectingTitle'),
                    'The world of collecting is bigger than ever'
                  )}
                </h2>
                <div
                  className={`mt-4 ${styles.borderBottomSecond}`}
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                    margin: '0 auto',
                  }}
                ></div>
              </div>
              <p
                className={`text-center m-0 p-0 ${styles.specialparaph}`}
                style={{
                  fontWeight: '500',
                  fontSize: '20px',
                  lineHeight: '25px',
                  maxWidth: 560,
                }}
              >
                {translationFallback(
                  'opportunityToExpandCollection',
                  t('opportunityToExpandCollection'),
                  'With us, you have a huge opportunity to create and expand your collection of the finest spirits'
                )}
              </p>
            </div>
            <div className={`d-flex flex-wrap ${styles.cardsPortfolio}`}>
              <div>
                <div>
                  <Image
                    src="/images/portfolio-guide/section2-icon1.svg"
                    alt="example"
                    width={0}
                    height={0}
                    sizes="100vw"
                    style={{ width: '115px', height: '115px', marginTop: 10 }}
                  />
                </div>
                <span>
                  {translationFallback(
                    'addToYourPortfolio',
                    t('addToYourPortfolio'),
                    'Add your own collection to your portfolio'
                  )}
                </span>
              </div>
              <div>
                <div>
                  <Image
                    src="/images/portfolio-guide/section2-icon2.svg"
                    alt="example"
                    width={0}
                    height={0}
                    sizes="100vw"
                    style={{ width: '115px', height: '115px', marginTop: 10 }}
                  />
                </div>
                <span>
                  {translationFallback(
                    'keep_track_collection',
                    tOther('keep_track_collection'),
                    'Keep track of your collection'
                  )}
                </span>
              </div>
              <div>
                <div>
                  <Image
                    src="/images/portfolio-guide/section2-icon3.svg"
                    alt="example"
                    width={0}
                    height={0}
                    sizes="100vw"
                    style={{ width: '115px', height: '115px', marginTop: 10 }}
                  />
                </div>
                <span>
                  {translationFallback(
                    'see_all_data',
                    tOther('see_all_data'),
                    'See all data and statistics of your collection 24/7'
                  )}
                </span>
              </div>
            </div>
          </div>
          <div
            className={`d-flex align-items-center justify-content-between gap-6 ${styles.firstSectionImage}`}
          >
            <div className="position-relative">
              <span className={styles.oneNumber}>1</span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback(
                    'portfolioPageIntro',
                    t('portfolioPageIntro'),
                    'On your portfolio page, you will find:'
                  )}
                </h2>
                <div
                  className={`mt-2 ${styles.borderDown}`}
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className={`mt-3 ${styles.secondSectionText}`}>
                <div className="d-flex mb-2">
                  <FontAwesomeIcon icon={faCheck} className="me-2 text-primary" />
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'collectionOverview',
                      t('collectionOverview'),
                      'An overview over your entire collection.'
                    )}
                  </p>
                </div>
                <div className="d-flex mb-2">
                  <FontAwesomeIcon icon={faCheck} className="me-2 text-primary" />
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'bottleDataAndMetrics',
                      t('bottleDataAndMetrics'),
                      'All datas and metrics of your bottles.'
                    )}
                  </p>
                </div>
                <div className="d-flex mb-2">
                  <FontAwesomeIcon icon={faCheck} className="me-2 text-primary" />
                  <p className="font-size-20 lh-23 text-left">
                    {translationFallback(
                      'inDepthCollectionTracking',
                      t('inDepthCollectionTracking'),
                      'In-Depth tracking of the progression of your collection overtime.'
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Image
                src="/images/portfolio-guide/section3-img.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
          </div>
        </div>
        <div className="bg-primary py-9">
          <div
            className={`d-flex align-items-center justify-content-between gap-6 ${styles.thirdSectionContent}`}
          >
            <div>
              <Image
                src="/images/portfolio-guide/section4-img.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
            <div className="position-relative d-flex flex-column">
              <span className={styles.twoNumber}>2</span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback(
                    'addingBottlesToCollection',
                    t('addingBottlesToCollection'),
                    'Adding bottles to your collection'
                  )}
                </h2>
                <div
                  className="mt-2"
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className="mt-3">
                <div className="d-flex mb-2">
                  <FontAwesomeIcon icon={faCheck} className="m-2" />
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'selectAddItemInstruction',
                      t('selectAddItemInstruction'),
                      'Simply select "Add item" and search for your desired bottle.'
                    )}
                  </p>
                </div>
                <div className="d-flex mb-2">
                  <FontAwesomeIcon icon={faCheck} className="m-2" />
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'automaticAdditionOnPurchase',
                      t('automaticAdditionOnPurchase'),
                      'When buying on Spiritory, we automatically add your purchased bottles to your collection.'
                    )}
                  </p>
                </div>
              </div>
              <button className={styles.whiteButton}>
                {translationFallback(
                  'addItemToPortfolioButton',
                  t('addItemToPortfolioButton'),
                  'Add Item to portfolio'
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="py-9">
          <div
            className={`d-flex align-items-center justify-content-between gap-6 ${styles.fourthSectionContent}`}
          >
            <div className="position-relative d-flex flex-column">
              <span className={styles.oneNumber}>3</span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback(
                    'enteringBottleDataTitle',
                    t('enteringBottleDataTitle'),
                    'Entering the data for your bottle'
                  )}
                </h2>
                <div
                  className="mt-2"
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className="mt-3">
                <div className="d-flex mb-2">
                  {translationFallback(
                    'enterBottleDetailsInstruction',
                    t('enterBottleDetailsInstruction'),
                    'In this step, you can enter the price you bought your bottle for as well as enter all the other information you want.'
                  )}
                </div>
              </div>
            </div>
            <div>
              <Image
                src="/images/portfolio-guide/section5-img.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
          </div>
        </div>
        <div
          className={`py-9 ${styles.fifthSectionContent}`}
          style={{ backgroundColor: '#C2AD7D' }}
        >
          <div style={{ width: 'fit-content' }} className="d-flex flex-column align-items-center">
            <h2
              className="h3 mt-3 text-center"
              style={{
                fontWeight: '400',
                fontSize: '32px',
                lineHeight: '40px',
              }}
            >
              {translationFallback(
                'specialCollectingTitle',
                t('specialCollectingTitle'),
                'What makes collecting with us special:'
              )}
            </h2>
            <div
              className="mt-2"
              style={{
                width: '70%',
                height: 4,
                backgroundColor: 'white',
                margin: '0 auto',
              }}
            ></div>
          </div>
          <div className="d-flex flex-wrap">
            <div
              className={`col d-flex flex-column justify-content-center align-items-center ${styles.portfolioCard}`}
              style={{ background: '#FFFFFF1A', borderRadius: '20px' }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />

              <p className="mx-2">
                {translationFallback(
                  'transparencyOnFees',
                  t('transparencyOnFees'),
                  '100% transparency on all fees'
                )}
              </p>
            </div>
            <div
              className={`col d-flex flex-column justify-content-center align-items-center ${styles.portfolioCard}`}
              style={{ background: '#FFFFFF1A', borderRadius: '20px' }}
            >
              <Image
                src="/images/sell-guide/gri2.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'quickAuthentication',
                  t('quickAuthentication'),
                  'Simple and quick authentication'
                )}
              </p>
            </div>
            <div
              className={`col d-flex flex-column justify-content-center align-items-center ${styles.portfolioCard}`}
              style={{ background: '#FFFFFF1A', borderRadius: '20px' }}
            >
              <Image
                src="/images/sell-guide/gri3.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'securePayoutProcess',
                  t('securePayoutProcess'),
                  'Safe and secure payout process'
                )}
              </p>
            </div>
            <div
              className={`col d-flex flex-column justify-content-center align-items-center ${styles.portfolioCard}`}
              style={{ background: '#FFFFFF1A', borderRadius: '20px' }}
            >
              <Image
                src="/images/sell-guide/gri4.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'inDepthPricingData',
                  t('inDepthPricingData'),
                  'In depth pricing data and sales statistics'
                )}
              </p>
            </div>
            <div
              className={`col d-flex flex-column justify-content-center align-items-center ${styles.portfolioCard}`}
              style={{ background: '#FFFFFF1A', borderRadius: '20px' }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'quickSellBuyProcess',
                  'quickSellBuyProcess',
                  'Sell and buy in just a few seconds, no hassle'
                )}
              </p>
            </div>
          </div>
        </div>
        <SearchInGuides
          becomeTitle={translationFallback(
            'becomeSeller',
            t('becomeSeller'),
            'Become a Seller right now'
          )}
          becomeCallToAct={translationFallback(
            'sellFirstBottle',
            t('sellFirstBottle'),
            'Sell your first bottle today'
          )}
          styling={{
            bgColor: '#497def',
            bTitleColor: '#fff',
            bCallToActColor: '#fff',
          }}
          userId={user?.id ?? null}
        />
        <div className={`pt-9 ${styles.hideDesktop}`}>
          <div className="d-flex flex-column align-items-center gap-3">
            <h2
              className="h3 mt-3"
              style={{
                fontWeight: '700',
                fontSize: '32px',
                lineHeight: '40px',
              }}
            >
              {translationFallback('faq', t('faq'), 'Frequently asked questions')}
            </h2>
          </div>
        </div>
        <div className={`pt-9 position-relative overflow-hidden ${styles.seventhContainer}`}>
          <div className={`${styles.seventhSectionContent}`}>
            <div>
              <div>
                <h2 className="h2" style={{ color: '#857040' }}>
                  {translationFallback(
                    'startCollection',
                    t('startCollection'),
                    'Start your collection now'
                  )}
                </h2>
                <p
                  style={{
                    fontWeight: '500',
                    fontSize: '20px',
                    lineHeight: '25px',
                  }}
                >
                  {translationFallback(
                    'signUpLoginInstruction',
                    t('signUpLoginInstruction'),
                    'Sign up or log in to your account to create your personalized collection'
                  )}
                </p>
                <div className={`d-flex align-items-center gap-4 ${styles.buttonsContainer}`}>
                  <div
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-up');
                      }
                    }}
                  >
                    <button className={`btn btn-s-outline-primary px-6`} type="button">
                      {translationFallback('signUpNow', t('signUpNow'), 'Sign Up Now')}
                    </button>
                  </div>
                  <div
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-in');
                      }
                    }}
                  >
                    <button className={`btn btn-s-primary my-2 px-6`} type="button">
                      {translationFallback('logInNow', t('logInNow'), 'Log In Now')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className={`${styles.seventhImage}`}>
              <Image
                src="/images/portfolio-guide/section8-img.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                className={`${styles.section8Img}`}
              />
            </div>
          </div>
        </div>
        <div className={`pt-9 pb-7 ${styles.hideMobile}`}>
          <div className="d-flex flex-column align-items-center gap-3">
            <h2
              className="h3 mt-3"
              style={{
                fontWeight: '700',
                fontSize: '32px',
                lineHeight: '40px',
              }}
            >
              {translationFallback('faq', t('faq'), 'Frequently asked questions')}
            </h2>
          </div>

          <div className={`container`}>
            <Accordion defaultActiveKey="0" className={'my-4'}>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <b>
                    {translationFallback(
                      'calculateMarketValue',
                      t('calculateMarketValue'),
                      'How do you calculate the market value of a bottle?'
                    )}
                  </b>
                </Accordion.Header>

                <Accordion.Body>
                  {translationFallback(
                    'spiritoryFormula',
                    t('spiritoryFormula'),
                    'We use the "Spiritory formula" to analyze sales data etc.'
                  )}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <b>
                    {translationFallback(
                      'autoAddPurchasedBottles',
                      t('autoAddPurchasedBottles'),
                      'Does Spiritory automatically add purchased bottles?'
                    )}
                  </b>
                </Accordion.Header>

                <Accordion.Body>
                  {translationFallback(
                    'bottlesAutomaticallyAdded',
                    t('bottlesAutomaticallyAdded'),
                    'Yes, your bottles are automatically added!'
                  )}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <b>
                    {translationFallback(
                      'payoutDuration',
                      t('payoutDuration'),
                      'How long does the payout take?'
                    )}
                  </b>
                </Accordion.Header>

                <Accordion.Body>
                  {translationFallback(
                    'payoutTimeframe',
                    t('payoutTimeframe'),
                    'Our payout usually takes 3-7, depending on the speed of the seller shipping the whisky.'
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps = getBasicStaticSideProps([
  'common',
  'portfolio_guide',
  'index',
  'other_translations',
]);

export default PortfolioGuide;
